import { useEffect, useMemo } from 'react';
import { type FieldError, useForm } from 'react-hook-form';

import { useIntl } from '../../hooks';
import { USER_ROLES } from '../../hooks/useUsers.js';
import { zodResolver } from '../../lib/zod-resolver.js';
import { createUserFormSchema, type UserFormValues } from '../../validations/user-form.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '../ui';
import { UserTagsField } from './UserTagsField.js';

type Mode = 'create' | 'edit';

type UserFormDialogProps = {
  open: boolean;
  mode: Mode;
  initialValues: UserFormValues;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: UserFormValues) => Promise<void | string>;
};

export default function AddUserDialog({
  open,
  mode,
  initialValues,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: UserFormDialogProps) {
  const intl = useIntl();
  const formSchema = useMemo(() => createUserFormSchema(mode), [mode]);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    control,
    formState: { errors, isValid },
  } = useForm<UserFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      reset(initialValues);
      clearErrors();
    }
  }, [open, initialValues, reset, clearErrors]);

  const onFormSubmit = handleSubmit(async (values) => {
    clearErrors('root');
    const result = await onSubmit({
      username: values.username.trim(),
      password: values.password,
      role: values.role,
      tags: values.tags,
    });

    if (typeof result === 'string' && result.length) {
      setError('root', {
        type: 'server',
        message: result,
      });
    }
  });

  const tagsError = errors.tags as FieldError | undefined;
  const tagsErrorMessage = typeof tagsError?.message === 'string' ? tagsError.message : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({
              id: mode === 'create' ? 'userDialog.title.add' : 'userDialog.title.edit',
            })}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onFormSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="username">
              {intl.formatMessage({ id: 'userDialog.field.username' })}
            </Label>
            <Input
              id="username"
              autoComplete="off"
              placeholder={intl.formatMessage({ id: 'userDialog.field.usernamePlaceholder' })}
              {...register('username')}
            />
            <p className="text-xs text-foreground">
              {intl.formatMessage({ id: 'userDialog.field.usernameHint' })}
            </p>
            {errors.username && (
              <p className="text-sm text-destructive">{errors.username.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">
              {intl.formatMessage({
                id:
                  mode === 'create'
                    ? 'userDialog.field.temporaryPassword'
                    : 'userDialog.field.resetPassword',
              })}
            </Label>
            <Input
              id="password"
              type="password"
              placeholder={intl.formatMessage({
                id:
                  mode === 'create'
                    ? 'userDialog.field.passwordPlaceholder'
                    : 'userDialog.field.passwordPlaceholderEdit',
              })}
              {...register('password')}
            />
            {mode === 'edit' && (
              <p className="text-xs text-foreground">
                {intl.formatMessage({ id: 'userDialog.field.passwordHintEdit' })}
              </p>
            )}
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="role">{intl.formatMessage({ id: 'userDialog.field.role' })}</Label>
            <select
              id="role"
              {...register('role')}
              className="h-10 rounded-md border border-border bg-input px-3 py-2 text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
            >
              {USER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {intl.formatMessage({ id: `userDialog.role.${role}` })}
                </option>
              ))}
            </select>
          </div>
          {errors.role && <p className="text-sm text-destructive">{errors.role.message}</p>}
          <div className="grid gap-2">
            <Label>{intl.formatMessage({ id: 'userDialog.field.tags' })}</Label>
            <UserTagsField control={control} disabled={isSubmitting} />
            {tagsErrorMessage ? (
              <p className="text-sm text-destructive">{tagsErrorMessage}</p>
            ) : null}
          </div>
          {errors.root && <div className="text-sm text-destructive">{errors.root.message}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {intl.formatMessage({ id: 'userDialog.action.cancel' })}
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting
                ? intl.formatMessage({
                    id:
                      mode === 'create' ? 'userDialog.action.creating' : 'userDialog.action.saving',
                  })
                : intl.formatMessage({
                    id: mode === 'create' ? 'userDialog.action.create' : 'userDialog.action.save',
                  })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export type { UserFormValues };
